"use client";

import { Input } from "@/components/ui";
import { ASSETS_BASE_PREFIX, BASE_URL } from "@/lib/constants";
import { appendTimestampToFilename, cn, formatImageUrl, sanitizeFilename } from "@/lib/utils";
import { Editor } from "@tiptap/react";
import { Loader2Icon, UploadCloudIcon } from "lucide-react";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

type ImageUploadProps = {
	editor: Editor | null;
	onClose: () => void;
};

const ImageUpload = ({ editor, onClose }: ImageUploadProps) => {
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [imageLink, setImageLink] = useState<string>("");

	const attachImageToContent = useCallback(
		(url: string, alt?: string) => {
			if (editor && url) {
				editor
					.chain()
					.focus()
					.setImage({ src: formatImageUrl(url), alt: alt ?? "Discussion Post Image" } as {
						src: string;
						alt: string;
					})
					.run();
				onClose();
			}
		},
		[editor, onClose]
	);

	const uploadImage = useCallback(
		async (file: File) => {
			try {
				setIsUploading(true);

				const encodedFileName = appendTimestampToFilename(sanitizeFilename(file.name));

				const inputObjectKey = `rich-text-editor-media/${encodedFileName}`;

				const url = new URL(`${BASE_URL}/api/presign`);
				url.searchParams.set("key", inputObjectKey);

				const res = await fetch(url);

				if (!res.ok) throw new Error("Failed to get presigned URL");

				const { preSignedUrl, objectPath } = await res.json();

				if (typeof objectPath !== "string") {
					throw new Error("Invalid object path");
				}

				const s3UploadResponse = await fetch(preSignedUrl, {
					method: "PUT",
					body: file,
				});
				if (!s3UploadResponse.ok) throw new Error("Failed to upload image to S3");

				const uploadedImageUrl = `${ASSETS_BASE_PREFIX}/${objectPath}`;
				attachImageToContent(uploadedImageUrl, encodedFileName || "Image");
			} catch (error) {
				console.error("Error uploading image:", error);

				toast.error("Failed to upload image");
			} finally {
				setIsUploading(false);
			}
		},
		[attachImageToContent]
	);

	const handleImageUpload = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			if (file) uploadImage(file);
		},
		[uploadImage]
	);

	const handleAddImage = () => {
		if (imageLink) {
			attachImageToContent(imageLink);
		} else {
			onClose();
		}
	};

	return (
		<div className="mx-auto w-full max-w-lg">
			<div
				className={cn(
					"group relative flex cursor-pointer flex-col items-center justify-center overflow-hidden rounded-3xl border py-4",
					isUploading && "bg-gray-100"
				)}
			>
				<div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200">
					<UploadCloudIcon className="h-6 w-6 text-gray-600" />
				</div>

				<p className="mb-1 mt-3 text-sm text-gray-500">
					<span className="text-brand-primary-600 font-semibold">Click to upload</span> or drag and
					drop
				</p>

				<span className="text-xs text-gray-500">PNG, JPG, JPEG or GIF (max. 1400x1400px)</span>

				<Input
					type="file"
					className="absolute inset-0 h-full w-full opacity-0"
					accept="image/x-png,image/jpeg,image/gif"
					onChange={handleImageUpload}
					disabled={isUploading}
				/>
			</div>

			<div className="my-4 flex items-center justify-center gap-2">
				<div className="h-px w-full bg-gray-200" />
				<div className="whitespace-nowrap text-sm text-gray-500">or use a link</div>
				<div className="h-px w-full bg-gray-200" />
			</div>

			<Input
				type="text"
				onChange={(e) => setImageLink(e.target.value)}
				placeholder="Enter Image link"
				className="block w-full rounded-md border px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
				disabled={isUploading}
			/>

			<button
				onClick={handleAddImage}
				className="mt-4 block w-full rounded-md bg-brand-primary py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
				disabled={isUploading}
			>
				{isUploading ? (
					<Loader2Icon className="mx-auto h-6 w-6 animate-spin stroke-white" />
				) : (
					"Add Image"
				)}
			</button>
		</div>
	);
};

export default ImageUpload;
